/**
 * Croc CX address — uniquely Croc opaque faces (v67).
 *
 * Industry (Sqids / History API):
 *   · deterministic URL-safe tokens (same path → same face forever)
 *   · migrate / canonize with history.replaceState (NO hashchange → no bar jitter)
 *   · push only for real navigations the user can Back from
 *   · never invent a destination; Soft≠Live for routing
 *
 * Emit:  `#/cx/<token>`  `#/cx/<parent>.<b64url(rest)>`  `#/cx/~<pack>`
 * Legacy `#/@/…` `#/~…` `#/_/…` plaintext → reveal + one-shot replaceState migrate.
 */

function norm(p) {
  let s = String(p == null ? "" : p).trim();
  if (!s) return "";
  if (s.startsWith("#")) s = s.slice(1);
  if (!s.startsWith("/")) s = "/" + s;
  if (s.length > 1 && s.endsWith("/")) s = s.slice(0, -1);
  return s;
}

export function readHashPath() {
  let raw = (typeof location !== "undefined" && location.hash || "").slice(1);
  if (!raw) return "";
  try {
    raw = decodeURIComponent(raw);
  } catch (_) { /* keep raw */ }
  return raw;
}

const ALPHA = "23456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
const SALT = "croc-cx-v67";
const FACE = "/cx/";
const LEGACY_AT = "/@/";
const LEGACY_PACK = "/_/";
const LEGACY_SALT = "c7x";

function fnv1a(str) {
  let h = 0x811c9dc5;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function toBase58(n, minLen) {
  let x = n >>> 0;
  const base = ALPHA.length;
  if (x === 0) {
    let z = ALPHA[0];
    while (z.length < minLen) z += ALPHA[0];
    return z.slice(0, minLen);
  }
  let out = "";
  while (x > 0) {
    out = ALPHA[x % base] + out;
    x = Math.floor(x / base);
  }
  while (out.length < minLen) out = ALPHA[0] + out;
  return out;
}

function mintToken(internalPath, saltExtra = "", salt = SALT) {
  const p = norm(internalPath) || "/ops";
  let h = fnv1a(salt + "|" + saltExtra + "|" + p);
  for (let i = 0; i < p.length; i++) {
    h ^= (p.charCodeAt(i) << ((i % 5) * 5)) >>> 0;
    h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0;
  }
  const h2 = fnv1a(String(h) + "|" + p + "|" + saltExtra);
  return (toBase58(h, 5) + toBase58(h2, 5)).slice(0, 10);
}

function b64urlEncode(str) {
  const bytes = unescape(encodeURIComponent(str));
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes.charCodeAt(i));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function b64urlDecode(tok) {
  let s = String(tok || "").replace(/-/g, "+").replace(/_/g, "/");
  while (s.length % 4) s += "=";
  const bin = atob(s);
  let bytes = "";
  for (let i = 0; i < bin.length; i++) bytes += "%" + ("00" + bin.charCodeAt(i).toString(16)).slice(-2);
  return decodeURIComponent(bytes);
}

function rev(s) {
  return String(s || "").split("").reverse().join("");
}

const ALIASES = Object.freeze({
  "/login": "/auth/login",
  "/forgot-password": "/auth/forgot",
  "/register": "/auth/signup",
  "/logout": "/auth/signout",
  "/signout": "/auth/signout",
  "/sign-out": "/auth/signout",
  "/home": "/ops",
  "/overview": "/ops",
  "/ops/map": "/ops",
  "/ops-live": "/machine?view=health",
  "/sa/ops": "/machine?view=health",
  "/devices": "/ops/devices",
  "/ops/plant": "/ops/devices",
  "/ops/run": "/ops/workbench",
  "/ops/alerts": "/ops/alarms",
  "/settings": "/settings/profile",
  "/account": "/settings/profile",
  "/account/cameras": "/cameras/manage",
  "/snapshots": "/cameras/retained",
  "/telegram": "/settings/integrate",
  "/voice": "/settings/integrate",
  "/sa-team": "/sa/team",
  "/fabric": "/settings/ai?lane=mcp",
  /* Soft≠Live Page39/40: legacy bookmark names → mounted Live desks (never demote Soft). */
  "/response-drill": "/prepare/drill",
  "/readiness": "/prepare/readiness",
  "/response-plans": "/prepare/playbook",
  "/response/playbook": "/prepare/playbook",
  "/incidents": "/response/cases",
  "/orchestration": "/response/cell",
  "/auto-sentinel": "/settings/ai",
  "/admin": "/settings/users",
  /* Soft≠Live Page46: Live compliance + delegations desks (never Soft demote → profile). */
  "/audit-log": "/settings/audit",
  "/platform-control": "/sa",
  "/tenant-delegations": "/settings/delegations",
  "/response/people": "/dispatch/people",
  "/people-online": "/dispatch/people",
  "/dispatch/assign": "/response/assign",
  /* Resources ≠ Response: roster / robots tools stay on Response; register lives under /dispatch. */
  "/response/playbooks": "/prepare/playbook",
  "/response/plans": "/prepare/playbook",
  /* PREP-IA-1: preparation owns readiness, drills, and playbooks. */
  "/response/drill": "/prepare/drill",
  "/response/readiness": "/prepare/readiness",
  "/response/sop": "/prepare/playbook",
  /* AUTO-1: Compat Rules Live under Automations Soft≠ invent Soft disappear. */
  "/response/rules": "/automations/legacy-rules",
  "/response/equipment": "/ops/resources?type=equipment",
  /* Response robots tool stays on Response (command). Resources register is /dispatch/robots. */
  "/response/automations": "/automations",
  "/automation": "/automations",
  "/automations/library": "/automations/library",
  "/automations/legacy-rules": "/automations/legacy-rules",
  /* Resources topbar register owns /dispatch/*; ontology stays typed at /ops/resources. */
  "/resources": "/dispatch/people",
  "/assets": "/dispatch/people",
  "/resources/people": "/dispatch/people",
  "/resources/cameras": "/ops/resources?type=camera",
  "/resources/robots": "/dispatch/robots",
  "/resources/equipment": "/ops/resources?type=equipment",
  "/resources/coverage": "/ops/resources?type=any",
  "/resources/readiness": "/ops/resources?type=any&view=readiness",
  "/ops/cameras": "/cameras/live",
  "/settings/cameras": "/cameras/manage",
  "/settings/models": "/settings/ai?lane=models",
  "/settings/fabric": "/settings/ai?lane=mcp",
  /* Machine L0/L1/M3-3 aliases — health bookmarks keep view= handoff */
  "/fleet/health": "/machine?view=health",
  "/fleet/diagnose": "/machine?view=health",
  "/fleet/groups": "/places/groups",
  "/fleet/zones": "/places/zones",
  "/fleet/external": "/dispatch/robots",
  "/external-fleet": "/dispatch/robots",
  "/robots": "/dispatch/robots",
  "/drones": "/dispatch/drones",
  "/machine/deployments": "/skills/assign",
  "/machine/capabilities": "/skills/assign",
  "/edge/skills": "/skills/library",
  "/group": "/places/groups",
  "/groups": "/places/groups",
  "/dispatch/scope": "/places/zones",
  "/response/actuators": "/response/equipment",
  "/settings/sites": "/places/sites",
  "/settings/estate": "/places/sites",
  "/settings/field": "/places/sites",
  /* Soft≠Live Page47: billing Live desk — never demote to vault/profile. */
  "/billing": "/settings/billing",
  "/sa/billing": "/settings/billing",
  /* OTA-1 / OTA-UX: Settings / firmware-admin / legacy Fleet firmware → dual-rail chooser, never auto-Base. */
  "/settings/ota": "/ota",
  "/firmware-admin": "/ota",
  "/fleet/firmware": "/ota",
  "/ota/capabilities": "/skills/waves",
  /* PLATFORM-UX: Control merges into Platform / Settings owners. */
  "/control/backup": "/sa/vault",
  "/control/custody": "/sa/custody",
  "/control/security": "/sa/caps",
  "/control/shares": "/settings/delegations",
  "/control/ng-shares": "/settings/delegations",
  "/control/domains": "/sa/tenant",
  "/control/federation": "/sa/tenant",
  "/control/reproduction": "/sa/audit",
  /* OPS3-5: Areas pathname → zones desk. Query stays on the hash (not alias keys). */
  "/ops/areas": "/ops/zones",
  "/areas": "/ops/zones",
  /* Standalone Machine deck — old Edge Unit tab bookmarks */
  "/edge/unit": "/machine",
  /* AIW-5: legacy /intel* hashes → Mission Room (desk file removed Soft≠ invent) */
  "/intel": "/response/cases",
  "/intel/reason": "/response/cases",
  "/intel/score": "/response/cases",
  "/intel/safety": "/response/cases",
  "/intel/ground": "/response/cases",
  "/intel/quality": "/response/cases",
  "/intel/gaps": "/response/cases",
  "/decide": "/response/cases",
});

export function resolveCanon(path) {
  const raw = String(path == null ? "" : path).trim();
  const qi = raw.indexOf("?");
  let base = norm(qi >= 0 ? raw.slice(0, qi) : raw);
  let q = qi >= 0 ? raw.slice(qi) : "";
  /* M3-2: Fleet solo device → Machine Level 1 */
  if (base.startsWith("/fleet/device/")) {
    const rest = base.slice("/fleet/device/".length);
    return "/machine/" + rest + q;
  }
  /* M3-3: Fleet group solo → Machine Groups */
  if (base.startsWith("/fleet/group/")) {
    const rest = base.slice("/fleet/group/".length);
    return "/machine/groups/" + rest + q;
  }
  if (base.startsWith("/group/")) {
    const rest = base.slice("/group/".length);
    return "/machine/groups/" + rest + q;
  }
  /* Alias → alias is forbidden as a leftover; walk to a fixed point. */
  for (let hop = 0; hop < 8; hop += 1) {
    const mapped = ALIASES[base];
    if (!mapped) break;
    const mqi = mapped.indexOf("?");
    const mbase = norm(mqi >= 0 ? mapped.slice(0, mqi) : mapped);
    const mq = mqi >= 0 ? mapped.slice(mqi) : "";
    if (mbase === base && (!mq || mq === q)) break;
    base = mbase;
    if (mq) q = mq;
  }
  const hit = base;
  /* Preserve query for typed Resources / handoff context (IA3-2). */
  if (!q) return hit;
  if (hit.includes("?")) return hit;
  return hit + q;
}

export { ALIASES as ROUTE_ALIASES };

/* Catalog = mounted Live desks only (must match main.js SUBS + SETTINGS_NAV). */
const CATALOG = Object.freeze([
  "/auth/login", "/auth/forgot", "/auth/signup", "/auth/signout",
  "/ops", "/ops/devices", "/ops/alarms", "/ops/zones",
  "/ops/dispatch", "/ops/workbench", "/ops/overview", "/ops/resources",
  "/dispatch", "/dispatch/overview", "/dispatch/people",
  "/dispatch/robots", "/dispatch/drones",
  "/places", "/places/sites", "/places/zones", "/places/groups",
  "/prepare", "/prepare/readiness", "/prepare/drill", "/prepare/playbook",
  "/automations", "/automations/library", "/automations/runs", "/automations/exceptions", "/automations/safety",
  "/automations/legacy-rules",
  "/skills", "/skills/library", "/skills/assign", "/skills/waves",
  "/cameras", "/cameras/live", "/cameras/retained", "/cameras/manage",
  "/ota", "/ota/base", "/ota/home",
  "/machine/groups",
  "/sa/edge-lab",
  "/response", "/response/board", "/response/assign", "/response/roster",
  "/response/rules", "/response/automations", "/response/cell", "/response/cases", "/response/drill",
  "/response/readiness", "/response/sop", "/response/equipment", "/response/robots",
  /* Fleet: firmware + zones still mounted; health/diagnose/external/groups → aliases only. */
  "/fleet", "/fleet/firmware", "/fleet/zones", "/fleet/device", "/fleet/group",
  /* Edge product city remains until PLATFORM-UX; Edge Lab exact path kept. */
  "/edge", "/edge/estate", "/edge/link", "/edge/share", "/edge/tunnel", "/edge/bench",
  "/edge/vision", "/edge/locate", "/edge/models", "/edge/runtime",
  "/edge/board", "/edge/mesh", "/edge/store", "/edge/security", "/edge/spatial",
  "/machine",
  "/activate", "/activate/identify", "/activate/verify", "/activate/claim",
  "/activate/interfaces", "/activate/capabilities", "/activate/position", "/activate/done",
  /* Intel/Decide city demounted — aliases only → Mission Room. */
  "/settings/profile", "/settings/security", "/settings/sessions",
  "/settings/delegations", "/settings/users", "/settings/notify", "/settings/alarm", "/settings/trigger",
  "/settings/ai",
  "/settings/integrate",
  "/settings/audit", "/settings/billing", "/settings/ota", "/settings/danger",
  "/control", "/control/domains", "/control/custody", "/control/ng-shares",
  "/control/federation", "/control/shares",
  "/control/security", "/control/backup", "/control/reproduction",
  "/sa", "/sa/caps", "/sa/models", "/sa/ai-tools", "/sa/team", "/sa/tenant", "/sa/custody",
  "/sa/factory", "/sa/audit", "/sa/vault",
  "/system", "/system/boot", "/system/404", "/system/403", "/system/502", "/system/500",
]);

function buildFaces(paths, salt) {
  const fwd = Object.create(null);
  const revMap = Object.create(null);
  for (const raw of paths) {
    const p = norm(raw);
    let n = 0;
    let tok = mintToken(p, "", salt);
    while (revMap[tok] && revMap[tok] !== p) {
      n += 1;
      tok = mintToken(p, "c" + n, salt);
      if (n > 64) throw new Error("face_collision:" + p);
    }
    fwd[p] = tok;
    revMap[tok] = p;
  }
  return { fwd, rev: revMap };
}

const { fwd: FACE_FWD, rev: FACE_REV } = buildFaces(CATALOG, SALT);
/** Reveal-only bridge for v64 `#/@/<token>` bookmarks. */
const { rev: FACE_REV_V64 } = buildFaces(CATALOG, "cmc-v64-opaque");

/** Retired SHORT — reveal-only; never emit. */
const LEGACY_SHORT = Object.freeze({
  "/auth/login": "/~",
  "/auth/forgot": "/~r",
  "/auth/signup": "/~n",
  "/auth/signout": "/~x",
  "/ops": "/~o",
  "/sa": "/~p",
  "/activate": "/~a",
  "/fleet": "/~f",
  "/response": "/~y",
  "/edge": "/~e",
  "/settings": "/~s",
  "/control": "/~k",
  "/system": "/~z",
  "/system/boot": "/~z/b",
  "/system/404": "/~z/4",
  "/system/403": "/~z/3",
  "/system/502": "/~z/5",
});

const LEGACY_SHORT_REV = Object.freeze(
  Object.fromEntries(Object.entries(LEGACY_SHORT).map(([k, v]) => [v, k])),
);

function legacyPack(internalPath) {
  const p = norm(internalPath) || "/ops";
  const core = LEGACY_SALT + "|" + p;
  const body = String(core.length) + "." + rev(b64urlEncode(core));
  return rev(b64urlEncode(body));
}

function legacyUnpack(tok) {
  const body = b64urlDecode(rev(String(tok || "")));
  const dot = body.indexOf(".");
  if (dot < 1) throw new Error("bad pack");
  const expect = Number(body.slice(0, dot));
  const coreB64 = rev(body.slice(dot + 1));
  const core = b64urlDecode(coreB64);
  if (core.length !== expect) throw new Error("len mismatch");
  if (!core.startsWith(LEGACY_SALT + "|")) throw new Error("salt");
  return norm(core.slice(LEGACY_SALT.length + 1));
}

function longestCatalogPrefix(internalPath) {
  const p = norm(internalPath) || "/ops";
  let best = "";
  for (const cand of CATALOG) {
    if (cand.length > best.length && (p === cand || p.startsWith(cand + "/"))) {
      best = cand;
    }
  }
  return best;
}

/** Public CX face keeps `?lane=` / handoff query. Never bake query into the token. */
function splitQuery(path) {
  const raw = String(path == null ? "" : path);
  const qi = raw.indexOf("?");
  if (qi < 0) return { path: raw, query: "" };
  return { path: raw.slice(0, qi), query: raw.slice(qi) };
}

function faceOfPath(p) {
  if (FACE_FWD[p]) return FACE + FACE_FWD[p];
  const parent = longestCatalogPrefix(p);
  if (parent && FACE_FWD[parent] && p !== parent) {
    const rest = p.slice(parent.length);
    return FACE + FACE_FWD[parent] + "." + b64urlEncode(rest);
  }
  return FACE + "~" + legacyPack(p);
}

export function cloak(internalPath) {
  const { path: rawPath, query } = splitQuery(internalPath);
  const resolved = resolveCanon(norm(rawPath) || "/ops");
  const { path: canonPath, query: canonQ } = splitQuery(resolved);
  return faceOfPath(canonPath) + (canonQ || query);
}

export function isCloaked(publicPath) {
  const h = norm(splitQuery(publicPath).path);
  if (h === "/cx" || h.startsWith("/cx/")) return true;
  if (h === "/@" || h.startsWith("/@/")) return true;
  if (h === "/_" || h.startsWith("/_/")) return true;
  if (h === "/~" || h.startsWith("/~")) return true;
  return false;
}

export function isModernFace(publicPath) {
  const h = norm(splitQuery(publicPath).path);
  return h === "/cx" || h.startsWith("/cx/");
}

function revealAtBody(body, fb, revMap = FACE_REV) {
  if (!body) return "/auth/login";
  if (body.startsWith("~")) {
    try {
      return resolveCanon(legacyUnpack(body.slice(1)) || fb);
    } catch (_) {
      return fb;
    }
  }
  const dot = body.indexOf(".");
  if (dot < 0) {
    return resolveCanon(revMap[body] || fb);
  }
  const parentTok = body.slice(0, dot);
  const restTok = body.slice(dot + 1);
  const parent = revMap[parentTok];
  if (!parent) return fb;
  try {
    const rest = b64urlDecode(restTok);
    return resolveCanon(norm(parent + (rest.startsWith("/") ? rest : "/" + rest)));
  } catch (_) {
    return resolveCanon(parent);
  }
}

export function reveal(publicPath, fallback = "/auth/login") {
  const { path: rawPath, query } = splitQuery(publicPath);
  const h = norm(rawPath);
  const fb = norm(fallback) || "/auth/login";
  let inner = fb;
  if (!h) {
    return fb + query;
  }

  /* Croc CX `#/cx/…` */
  if (h === "/cx" || h.startsWith("/cx/")) {
    inner = revealAtBody(h === "/cx" ? "" : h.slice(FACE.length), fb);
  } else if (h === "/@" || h.startsWith("/@/")) {
    /* Legacy v64 `#/@/…` — reveal via v64 table; emit migrates to `#/cx/…`. */
    const body = h === "/@" ? "" : h.slice(LEGACY_AT.length);
    inner = revealAtBody(body, fb, FACE_REV_V64);
  } else if (h === "/_" || h.startsWith("/_/")) {
    const tok = h === "/_" ? "" : h.slice(LEGACY_PACK.length);
    if (!tok) inner = "/auth/login";
    else {
      try {
        inner = resolveCanon(legacyUnpack(tok) || fb);
      } catch (_) {
        inner = fb;
      }
    }
  } else if (LEGACY_SHORT_REV[h]) {
    inner = resolveCanon(LEGACY_SHORT_REV[h]);
  } else {
    let matched = false;
    for (const [face, mapped] of Object.entries(LEGACY_SHORT_REV)) {
      for (const sep of ["/.", "/+"]) {
        const mark = face + sep;
        if (h.startsWith(mark)) {
          matched = true;
          try {
            const rest = b64urlDecode(h.slice(mark.length));
            inner = resolveCanon(norm(mapped + (rest.startsWith("/") ? rest : "/" + rest)));
          } catch (_) {
            inner = fb;
          }
        }
      }
    }
    if (!matched) {
      if (h === "/~/" || h.startsWith("/~/")) {
        const tok = h.slice(3);
        if (!tok) inner = "/auth/login";
        else {
          try {
            inner = resolveCanon(norm(b64urlDecode(tok)) || fb);
          } catch (_) {
            inner = fb;
          }
        }
      } else {
        inner = resolveCanon(h);
      }
    }
  }

  const { path: innerPath, query: innerQ } = splitQuery(inner);
  return innerPath + (innerQ || query);
}

export function hrefOf(internalPath) {
  return "#" + cloak(internalPath);
}

/**
 * Single address-bar writer. Returns false when already at face (no jitter).
 * mode: "replace" → history.replaceState (no hashchange)
 *       "push"    → location.hash (one hashchange, Back works)
 */
export function writeFace(face, mode = "push") {
  if (typeof location === "undefined") return false;
  const want = norm(face);
  if (!want) return false;
  const cur = norm(readHashPath());
  if (cur === want) return false;
  const url = location.pathname + location.search + "#" + want;
  if (mode === "replace" && typeof history !== "undefined" && history.replaceState) {
    try {
      history.replaceState(history.state, "", url);
      return "replaced";
    } catch (_) {
      /* fall through */
    }
  }
  location.hash = want;
  return "pushed";
}

let _rewriteLock = false;

export function ensureCloakedHash(fallback = "/auth/login") {
  if (_rewriteLock) {
    return reveal(readHashPath(), fallback);
  }
  const fb = norm(fallback) || "/auth/login";
  const raw = readHashPath();
  const inner = raw ? reveal(raw, fb) : fb;
  const face = cloak(inner);
  if (norm(raw) === face) return inner;
  _rewriteLock = true;
  try {
    writeFace(face, "replace");
  } finally {
    _rewriteLock = false;
  }
  return inner;
}

export { FACE, FACE_FWD, FACE_REV, FACE_REV_V64, CATALOG, SALT };

export const ROUTE_MASK = {
  cloak, reveal, hrefOf, isCloaked, isModernFace, ensureCloakedHash,
  writeFace, readHashPath, resolveCanon, FACE, FACE_FWD, FACE_REV, FACE_REV_V64,
  CATALOG, SALT,
};

(function assertUniqueFaces() {
  const seen = new Set();
  for (const p of CATALOG) {
    const f = FACE_FWD[p];
    if (!f || seen.has(f)) throw new Error("route_face_not_unique:" + p);
    seen.add(f);
  }
})();
